// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { DefaultRawDatum, PieCustomLayerProps, ResponsivePie } from '@nivo/pie';
// Components, Layouts, Pages
import { Loading } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBasePieData } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { IPersonalCloseDate } from '~/utils/interface/statistic';
import { getStatisticQuoteConverted } from '~/thunks/statistic/statisticThunk';
// Styles, images, icons
import styles from './PersonalCloseDate.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const PersonalCloseDate = (props: Props) => {
  //#region Destructuring Props
  const {} = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/dashboard']);
  const dispatch = useAppDispatch();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [data, setData] = useState<IPersonalCloseDate>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    handleGetDataQuoteConverted();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDataQuoteConverted = () => {
    setIsLoading(true);

    dispatch(getStatisticQuoteConverted())
      .unwrap()
      .then((res) => {
        const { data } = res;

        if (!data) return;

        setData(data);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const convertPieChartData: IBasePieData[] = useMemo(() => {
    return [
      {
        id: 'Quotes Sent',
        label: '',
        value: data?.totalSend || 0,
        color: '#FFA111',
        percent: data?.totalSend || 0,
      },
      {
        id: 'Quotes Converted',
        label: '',
        value: data?.totalConverted || 0,
        color: '#19D37D',
        percent: data?.totalConverted || 0,
      },
    ];
  }, [data]);

  const renderCenterIcon = (data: PieCustomLayerProps<DefaultRawDatum>) => {
    const { centerX, centerY } = data;

    return (
      <g transform={`translate(${centerX},${centerY})`}>
        <circle r={34} fill='#E8FBF2' />

        <image href={icons.commonIconPie} height={30} width={30} x={-15} y={-15} />
      </g>
    );
  };
  //#endregion Handle Function

  return (
    <Box id='personalCloseDateComponent' className={cx('container')}>
      <Stack direction={'row'} spacing={'13px'} justifyContent={'space-between'} justifyItems={'center'}>
        <Typography className={cx('title')}>{t('dashboard_title_personal_close_date')}</Typography>

        <Typography className={cx('subTitle')}>
          {data?.totalSend
            ? `${(((data?.totalConverted || 0) / (data?.totalSend || 0)) * 100).toFixed(0).toString()}%`
            : '0%'}
        </Typography>
      </Stack>

      <Stack direction={'column'} gap={7}>
        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <Box className={cx('boxSent')} />

            <Typography className={cx('label')}>{t('personal_close_rate_title_quotes_sent')}</Typography>
          </Stack>

          <Typography className={cx('value')}>{data?.totalSend || 0}</Typography>
        </Stack>

        <Stack direction={'row'} alignItems={'center'} justifyContent={'space-between'}>
          <Stack direction={'row'} alignItems={'center'} gap={2}>
            <Box className={cx('boxConverted')} />

            <Typography className={cx('label')}>{t('personal_close_rate_title_quotes_converted')}</Typography>
          </Stack>

          <Typography className={cx('value')}>{data?.totalConverted || 0}</Typography>
        </Stack>
      </Stack>

      <Stack className={cx('pieComponent')}>
        <ResponsivePie
          data={convertPieChartData}
          innerRadius={0.8}
          colors={{ datum: 'data.color' }}
          enableArcLinkLabels={false}
          arcLabel={EMPTY_STRING}
          borderWidth={1}
          borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
          layers={['arcs', 'arcLabels', 'arcLinkLabels', renderCenterIcon]}
        />
      </Stack>

      {isLoading && <Loading />}
    </Box>
  );
};

export default PersonalCloseDate;
